// D-0118 (Rafael 27/10): "Seria bom ter uma tela para ver os comprovantes e poder tb excluir."
// Documentos de uma parcela (recibo, comprovante, NF): abrir, substituir e excluir, com quem enviou.
// - Lista: GET financeiroParcela/{id}/arquivos (tabela 1:N; o "Atual" é o principal).
// - Substituir/anexar: POST financeiroParcelaArquivo/{id} (o anterior fica como "Anterior").
// - Excluir: DELETE financeiroParcela/{id}/arquivos/{anexoId} — sai da lista e fica no histórico
//   da parcela; no recibo do adiantamento sai de todas as parcelas do evento.
// A regra de quem pode vem por props (`podeGerenciar`); a API repete a trava.
import React, { useState, useEffect, useCallback } from 'react';
import { FaFileAlt, FaFileInvoiceDollar, FaReceipt, FaRegFileAlt, FaTimes, FaTrashAlt } from 'react-icons/fa';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { ACEITE_RECIBO, erroSeNaoForPdf } from '../../util/util';
import { FinAjuda } from '../FinField';
import '../../css/financeiro-parcela-pagar.css';

export const TIPOS_DOC_ADIANTAMENTO = [
    { key: 'reciboadiantamento', rotulo: 'Recibo', icone: <FaReceipt size={13} />, pasta: 'reciboadiantamento', exigePdf: true },
    { key: 'comprovantepagamento', rotulo: 'Comprovante', icone: <FaFileInvoiceDollar size={13} />, pasta: 'comprovantepagamento' },
    { key: 'notafiscal', rotulo: 'Nota Fiscal', icone: <FaFileAlt size={13} />, somenteLeitura: true },
];

const msgErro = (e, padrao) => (e && e.response && e.response.data && (e.response.data.error || e.response.data.mensagem)) || padrao;

export default function DocumentosParcela({ parcelaId, tipos = TIPOS_DOC_ADIANTAMENTO, podeGerenciar = false, avisoSemPermissao = '', onAlterado }) {
    const token = localStorage.getItem('token');
    const [arquivos, setArquivos] = useState(null);
    const [enviando, setEnviando] = useState(null);      // key do tipo em envio
    const [excluir, setExcluir] = useState(null);        // { tipo, doc, salvando }

    const carregar = useCallback(() => {
        if (!parcelaId) return;
        api({ method: 'get', action: '/financeiroParcela/' + parcelaId + '/arquivos', token: token })
            .then((r) => setArquivos((r.data && r.data.data) || {}))
            .catch(() => setArquivos({}));
    }, [parcelaId, token]);

    useEffect(() => { carregar(); }, [carregar]);

    const depoisDeMudar = () => { carregar(); if (onAlterado) onAlterado(); };

    const abrir = async (doc) => {
        try {
            const resp = await axios.get(process.env.REACT_APP_API_URL + '/arquivo/download/' + doc.arquivo_id,
                { headers: { Authorization: 'Bearer ' + token }, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([resp.data]));
            const a = document.createElement('a');
            a.href = url;
            a.setAttribute('download', doc.nome || 'documento');
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error('Erro ao baixar o arquivo.');
        }
    };

    const enviar = (tipo) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.style.display = 'none';
        if (tipo.exigePdf) input.accept = ACEITE_RECIBO;
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (!file) return;
            if (tipo.exigePdf) {
                const erroPdf = erroSeNaoForPdf(file);
                if (erroPdf) { toast.error(erroPdf); return; }
            }
            setEnviando(tipo.key);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('tipoArquivo', tipo.key);
            formData.append('modulo', 'financeiro');
            formData.append('pasta', tipo.pasta || tipo.key);
            axios.post(process.env.REACT_APP_API_URL + '/financeiroParcelaArquivo/' + parcelaId, formData,
                { headers: { 'content-type': 'multipart/form-data', Authorization: 'Bearer ' + token } })
                .then(() => { toast.success(tipo.rotulo + ' enviado.'); depoisDeMudar(); })
                .catch((err) => toast.error(msgErro(err, 'Erro ao enviar o arquivo.')))
                .finally(() => setEnviando(null));
        };
        document.body.appendChild(input);
        input.click();
        document.body.removeChild(input);
    };

    const confirmarExclusao = () => {
        const ex = excluir;
        setExcluir(Object.assign({}, ex, { salvando: true }));
        api({ method: 'delete', action: '/financeiroParcela/' + parcelaId + '/arquivos/' + ex.doc.id, token: token })
            .then(() => { toast.success(ex.tipo.rotulo + ' excluído.'); setExcluir(null); depoisDeMudar(); })
            .catch((err) => {
                toast.error(msgErro(err, 'Erro ao excluir o documento.'));
                setExcluir((prev) => (prev ? Object.assign({}, prev, { salvando: false }) : prev));
            });
    };

    if (arquivos === null) return <div className="fin-pp__empty" style={{ padding: '28px 0' }}>Carregando documentos…</div>;

    const linhas = [];
    tipos.forEach((t) => (arquivos[t.key] || []).forEach((d) => linhas.push({ tipo: t, doc: d })));
    const tiposEditaveis = tipos.filter((t) => !t.somenteLeitura);
    const bloqueio = !podeGerenciar ? (avisoSemPermissao || 'Sem permissão para alterar documentos.') : null;

    return (
        <>
            {linhas.length === 0 ? (
                <div className="fin-pp__empty" style={{ padding: '28px 0' }}>Nenhum documento anexado.</div>
            ) : (
                <div className="fin-pp__tblwrap">
                    <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                        <thead><tr>
                            <th>Tipo</th>
                            <th>Documento</th>
                            <th>Enviado</th>
                            <th style={{ textAlign: 'right' }}>Ações</th>
                        </tr></thead>
                        <tbody>
                            {linhas.map((l) => {
                                const editavel = !l.tipo.somenteLeitura;
                                return (
                                    <tr key={l.tipo.key + '_' + l.doc.id}>
                                        <td className="fin-pp__cell-muted" style={{ whiteSpace: 'nowrap' }}>{l.tipo.icone} {l.doc.rotulo_tipo || l.tipo.rotulo}</td>
                                        <td className="fin-pp__cell-strong fin-pp__ellipsis" title={l.doc.nome}>
                                            {l.doc.nome}
                                            <span className={'fin-pp__minitag ' + (l.doc.principal ? 'is-ok' : 'is-info')} style={{ marginLeft: 6 }}
                                                title={l.doc.principal ? 'É o documento que vale hoje' : 'Substituído por um mais novo — fica guardado'}>
                                                {l.doc.principal ? 'Atual' : 'Anterior'}
                                            </span>
                                        </td>
                                        <td className="fin-pp__cell-muted">
                                            <div style={{ whiteSpace: 'nowrap' }}>{l.doc.created_at ? moment(l.doc.created_at).format('DD/MM/YYYY HH:mm') : '—'}</div>
                                            {l.doc.enviado_por_nome && <div style={{ fontSize: '0.78rem' }}>{l.doc.enviado_por_nome}</div>}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={() => abrir(l.doc)}>Abrir</button>
                                            {editavel && l.doc.principal && (
                                                <button type="button" className="fin-pp__btn fin-pp__btn--sm" style={{ marginLeft: 6 }}
                                                    disabled={!!bloqueio || enviando === l.tipo.key}
                                                    title={bloqueio || 'Envia um arquivo novo; este fica como "Anterior"'}
                                                    onClick={() => enviar(l.tipo)}>
                                                    {enviando === l.tipo.key ? 'Enviando…' : 'Substituir'}
                                                </button>
                                            )}
                                            {editavel && (
                                                <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost" style={{ marginLeft: 6 }}
                                                    disabled={!!bloqueio}
                                                    title={bloqueio || 'Tira da lista; fica no histórico da parcela'}
                                                    onClick={() => setExcluir({ tipo: l.tipo, doc: l.doc, salvando: false })}>
                                                    <FaTrashAlt size={11} /> Excluir
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Anexar quando o tipo ainda não tem documento (primeiro envio é livre, como antes). */}
            {tiposEditaveis.some((t) => !(arquivos[t.key] || []).some((d) => d.principal)) && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '12px 14px', borderTop: linhas.length ? '1px solid var(--fin-bd)' : 'none' }}>
                    {tiposEditaveis.filter((t) => !(arquivos[t.key] || []).some((d) => d.principal)).map((t) => (
                        <button key={t.key} type="button" className="fin-pp__btn fin-pp__btn--sm"
                            disabled={enviando === t.key} onClick={() => enviar(t)}>
                            {enviando === t.key ? 'Enviando…' : 'Anexar ' + t.rotulo.toLowerCase()}
                        </button>
                    ))}
                </div>
            )}
            {bloqueio && linhas.some((l) => !l.tipo.somenteLeitura) && (
                <div className="fin-pp__cell-muted" style={{ fontSize: '0.78rem', padding: '8px 14px' }}>
                    {bloqueio} <FinAjuda texto={bloqueio} />
                </div>
            )}

            {excluir && (
                <div className="modal fade show d-block fin-pp fin-pp__modalbg" style={{ zIndex: 1070 }}
                    onClick={(e) => { if (e.target === e.currentTarget && !excluir.salvando) setExcluir(null); }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content fin-pp__modal">
                            <div className="fin-pp__modal-head">
                                <span className="fin-pp__modal-head-ic is-danger"><FaTrashAlt size={16} /></span>
                                <h3 className="fin-pp__modal-title">Excluir {excluir.tipo.rotulo.toLowerCase()}
                                    <span className="fin-pp__modal-sub">{excluir.doc.nome}</span>
                                </h3>
                                <button type="button" className="fin-pp__modal-close" disabled={excluir.salvando} onClick={() => setExcluir(null)}><FaTimes size={16} /></button>
                            </div>
                            <div className="modal-body" style={{ fontSize: '0.88rem' }}>
                                O arquivo sai da lista e a exclusão fica no histórico da parcela, com seu nome e a hora.
                                {excluir.tipo.key === 'reciboadiantamento' && ' O recibo é do evento: sai de todas as parcelas de adiantamento dele.'}
                                {excluir.doc.principal && ' Se houver um anterior, ele volta a valer.'}
                            </div>
                            <div className="fin-pp__modal-foot">
                                <button type="button" className="fin-pp__btn" disabled={excluir.salvando} onClick={() => setExcluir(null)}>Cancelar</button>
                                <button type="button" className="fin-pp__btn fin-pp__btn--danger" disabled={excluir.salvando} onClick={confirmarExclusao}>
                                    {excluir.salvando ? 'Excluindo…' : 'Excluir'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// Mesma lista dentro de um modal .fin-pp (lista de adiantamentos, Contas a Receber).
export function DocumentosParcelaModal({ parcela, titulo = 'Documentos', subtitulo, fechar, ...resto }) {
    if (!parcela) return null;
    return (
        <div className="modal fade show d-block fin-pp fin-pp__modalbg" style={{ zIndex: 1060 }}
            onClick={(e) => { if (e.target === e.currentTarget) fechar(); }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content fin-pp__modal">
                    <div className="fin-pp__modal-head">
                        <span className="fin-pp__modal-head-ic"><FaRegFileAlt size={17} /></span>
                        <h3 className="fin-pp__modal-title">{titulo}
                            <span className="fin-pp__modal-sub">{subtitulo || parcela.descricao || 'Adiantamento'}</span>
                        </h3>
                        <button type="button" className="fin-pp__modal-close" onClick={fechar}><FaTimes size={16} /></button>
                    </div>
                    <div className="modal-body" style={{ padding: 0 }}>
                        <DocumentosParcela parcelaId={parcela.id} {...resto} />
                    </div>
                    <div className="fin-pp__modal-foot">
                        <button type="button" className="fin-pp__btn" onClick={fechar}>Fechar</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
